import {
  ALLOWED_DOCUMENT_MIME_TYPES,
  normalizeDocumentMime,
  AcceptOfferResponseSchema,
  CONSENT_TEXTS,
  ConsentActionResponseSchema,
  CreditApplicationSchema,
  CustomerMemoryResponseSchema,
  EvidenceDocumentSchema,
  OfferSchema,
  ScaChallengeSchema,
  SignFormalizationResponseSchema,
  type ConsentType,
  type EvidenceType,
} from '@paguz/contracts';
import { z } from 'zod';
import { apiRequest } from './client.ts';
import { isApiError } from './errors.ts';

/** Originación (`/v1/applications/*`): expediente, documentos, oferta y formalización. */

/** Solicitud activa del cliente o `null` si no tiene (`404 PZ-APP-404`). */
export async function getCurrentApplication() {
  try {
    return await apiRequest('/v1/applications/current', CreditApplicationSchema);
  } catch (error) {
    if (isApiError(error) && (error.reasonCode === 'PZ-APP-404' || error.status === 404)) {
      return null;
    }
    throw error;
  }
}

export function getApplication(id: string) {
  return apiRequest(`/v1/applications/${encodeURIComponent(id)}`, CreditApplicationSchema);
}

export function createApplication(idempotencyKey: string) {
  return apiRequest('/v1/applications', CreditApplicationSchema, {
    method: 'POST',
    body: { channel: 'WEB' },
    idempotencyKey,
  });
}

/** Formatos y tamaño permitidos (`PZ-DOC-001`, `PZ-DOC-002`). */
export const DOCUMENT_ACCEPT = [...ALLOWED_DOCUMENT_MIME_TYPES, '.xlsx', '.csv'].join(',');
export const MAX_DOCUMENT_BYTES = 15 * 1024 * 1024;
const ALLOWED = new Set<string>(ALLOWED_DOCUMENT_MIME_TYPES);

/** Valida el archivo antes de subirlo; devuelve el mensaje para el cliente o `null`. */
export function documentProblem(file: File): string | null {
  if (/\.xls$/i.test(file.name))
    return 'Ese Excel es de una versión antigua. Ábrelo y guárdalo como «Libro de Excel (.xlsx)» o en PDF, y súbelo de nuevo.';
  if (!ALLOWED.has(normalizeDocumentMime(file.name, file.type)))
    return 'Ese formato no lo podemos leer. Sube una foto (JPG o PNG), un PDF o un Excel (.xlsx o .csv).';
  if (file.size > MAX_DOCUMENT_BYTES)
    return 'El archivo pesa más de 15 MB. Toma la foto de nuevo o sube un PDF más liviano.';
  if (file.size === 0) return 'El archivo está vacío. Inténtalo de nuevo.';
  return null;
}

/** `POST /v1/applications/{id}/documents` (multipart). La revisión IA llega después. */
export function uploadDocument(
  applicationId: string,
  file: File,
  declaredType: EvidenceType | null,
  idempotencyKey: string,
) {
  const form = new FormData();
  // El tipo según la extensión (Windows reporta un .csv como Excel antiguo; algunos teléfonos, sin tipo).
  const type = normalizeDocumentMime(file.name, file.type);
  form.set('file', type === file.type ? file : new File([file], file.name, { type }), file.name);
  if (declaredType !== null) form.set('declaredType', declaredType);
  return apiRequest(
    `/v1/applications/${encodeURIComponent(applicationId)}/documents`,
    EvidenceDocumentSchema,
    { method: 'POST', rawBody: form, idempotencyKey },
  );
}

export function listDocuments(applicationId: string) {
  return apiRequest(
    `/v1/applications/${encodeURIComponent(applicationId)}/documents`,
    z.object({ data: z.array(EvidenceDocumentSchema) }),
  );
}

export function getOffer(applicationId: string) {
  return apiRequest(`/v1/applications/${encodeURIComponent(applicationId)}/offer`, OfferSchema);
}

/** Reto SCA para aceptar la oferta (`POST /v1/sca/challenges`, `OFFER_ACCEPTANCE`). */
export function createOfferChallenge(offerId: string) {
  return apiRequest('/v1/sca/challenges', ScaChallengeSchema, {
    method: 'POST',
    body: { operationType: 'OFFER_ACCEPTANCE', operationId: offerId, method: 'OTP' },
  });
}

export function acceptOffer(applicationId: string, scaToken: string, idempotencyKey: string) {
  return apiRequest(
    `/v1/applications/${encodeURIComponent(applicationId)}/offer/accept`,
    AcceptOfferResponseSchema,
    { method: 'POST', body: { scaToken }, idempotencyKey },
  );
}

/** Firma del pagaré con el OTP de firma electrónica → línea `ACTIVE`. */
export function signFormalization(applicationId: string, code: string, idempotencyKey: string) {
  return apiRequest(
    `/v1/applications/${encodeURIComponent(applicationId)}/formalization/sign`,
    SignFormalizationResponseSchema,
    { method: 'POST', body: { code }, idempotencyKey },
  );
}

/** Acepta una autorización con el texto vigente (`POST /v1/applications/{id}/consents`). */
export function acceptConsent(applicationId: string, type: ConsentType) {
  return apiRequest(
    `/v1/applications/${encodeURIComponent(applicationId)}/consents`,
    ConsentActionResponseSchema,
    {
      method: 'POST',
      body: { type, accepted: true, textVersion: CONSENT_TEXTS[type]?.version ?? '' },
      idempotencyKey: crypto.randomUUID(),
    },
  );
}

/** Datos que el asesor anotó y esperan confirmación del cliente (`GET /v1/customers/me/memory`). */
export async function listProposedFacts() {
  const page = await apiRequest('/v1/customers/me/memory', CustomerMemoryResponseSchema, {
    query: { status: 'PROPOSED' },
  });
  return page.data;
}

/** Confirma en pantalla los datos críticos (`POST /v1/customers/me/memory/confirm`). */
export function confirmFacts(factIds: readonly string[]) {
  return apiRequest('/v1/customers/me/memory/confirm', z.unknown(), {
    method: 'POST',
    body: { factIds },
  });
}

/** El cliente confirma en pantalla las cifras del flujo de caja (`POST …/cash-flow/confirm`). */
export function confirmCashFlow(applicationId: string, version: number) {
  return apiRequest(
    `/v1/applications/${encodeURIComponent(applicationId)}/cash-flow/confirm`,
    z.unknown(),
    { method: 'POST', body: { version }, idempotencyKey: crypto.randomUUID() },
  );
}
